import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { LogoFull } from "./Logo";
import { scrollToSection } from "@/lib/scroll";
import { waLink } from "@/lib/constants";

const LINKS = [
  { label: "Serviços", hash: "#servicos", testid: "nav-servicos" },
  { label: "Balanceamento e Geometria", hash: "#balanceamento", testid: "nav-balanceamento" },
  { label: "Troca de Óleo", hash: "#troca-de-oleo", testid: "nav-troca-de-oleo" },
  { label: "Freios", hash: "#freios", testid: "nav-freios" },
  { label: "Sobre Nós", hash: "#sobre", testid: "nav-sobre" },
  { label: "Contato", hash: "#contato", testid: "nav-contato" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const close = () => setOpen(false);
    window.addEventListener("resize", close);
    return () => window.removeEventListener("resize", close);
  }, []);

  const go = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(hash);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-ink/95 backdrop-blur-md border-b border-white/[0.08]">
      <div className="px-4 sm:px-6 lg:px-10 max-w-[1400px] mx-auto h-[76px] flex items-center justify-between gap-6">
        <button
          data-testid="logo-button"
          onClick={(e) => go(e, "#inicio")}
          className="flex items-center gap-3 shrink-0"
          aria-label="Ir para o início"
        >
          <LogoFull testid="navbar-logo" />
          <span className="hidden sm:block font-mono text-[10px] leading-tight tracking-[0.25em] text-volt text-left uppercase">
            Balneário
            <br />
            Camboriú · SC
          </span>
        </button>

        <nav className="hidden xl:flex items-center gap-7">
          {LINKS.map((l) => (
            <a
              key={l.hash}
              href={l.hash}
              data-testid={l.testid}
              onClick={(e) => go(e, l.hash)}
              className="font-display font-semibold uppercase tracking-wide text-[15px] text-gray-200 hover:text-signal transition-colors duration-300 whitespace-nowrap"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a
          data-testid="header-whatsapp-button"
          href={waLink()}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden xl:inline-flex btn-cut items-center gap-2 px-6 py-3 bg-signal hover:bg-signalhover text-white font-display font-bold uppercase tracking-wide text-[15px] transition-colors duration-300 whitespace-nowrap"
        >
          <MessageCircle className="h-4 w-4" />
          Agendar pelo WhatsApp
        </a>

        <button
          data-testid="navbar-mobile-menu-button"
          className="xl:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="xl:hidden overflow-hidden bg-ink/98 border-b border-white/[0.08]"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.hash}
                  href={l.hash}
                  data-testid={`${l.testid}-mobile`}
                  onClick={(e) => go(e, l.hash)}
                  className="font-display font-semibold uppercase tracking-wide text-lg text-gray-200 hover:text-signal transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <a
                data-testid="header-whatsapp-button-mobile"
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-cut mt-2 inline-flex w-fit items-center gap-2 px-6 py-3 bg-signal text-white font-display font-bold uppercase tracking-wide"
              >
                <MessageCircle className="h-4 w-4" />
                Agendar pelo WhatsApp
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
